import { useEffect, useState } from "react";

import { Destination } from "Constant/destination";

import { createDestination } from "helpers/destinations/createDestination";
import {
    getCreatedOpenAiDestinationIds,
    getOpenAiAdsConversions,
    postOpenAiAdsMappings,
} from "helpers/destinations/openaiAds";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";
import {
    areOpenAiMappingsValid,
    buildOpenAiMappingsPayload,
    normalizeAutoMatchedEvents,
} from "components/organisms/Destinations/OpenAiAds/OpenAiAds.helpers";
import SwalAlertComp from "components/SwalAlert";

import { cannotBeBlank } from "../DestinationCreation.helpers";

/**
 * OpenAI Ads Conversions API: pixel id + API keys, create (conversions and
 * optional pixel destination), then map events to conversions.
 */
const useOpenAiDestination = ({
    language,
    form,
    setSaveDisabled,
    setModalLoading,
    completeCreation,
    hostActions,
    registerFooterAction,
}) => {
    const [destinationId, setDestinationId] = useState(null);
    const [pixelDestinationId, setPixelDestinationId] = useState(null);
    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [conversionsError, setConversionsError] = useState(null);
    const [isMappingOpen, setIsMappingOpen] = useState(false);
    const [isMappingCompleted, setIsMappingCompleted] = useState(false);

    const { destinationCode, credentials } = form;
    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const getCreatedIds = () =>
        [destinationId, pixelDestinationId].filter((id) => id != null);

    const resetOnTypeChange = () => {
        setDestinationId(null);
        setPixelDestinationId(null);
        setConversions([]);
        setEvents([]);
        setMappings([]);
        setConversionsError(null);
        setIsMappingOpen(false);
        setIsMappingCompleted(false);
    };

    useEffect(() => {
        if (isMappingOpen || destinationCode !== Destination.oaicapi) return;

        setSaveDisabled(
            !credentials?.pixel_id ||
                !credentials?.conversions_api_key ||
                !credentials?.advertiser_api_key,
        );
    }, [destinationCode, credentials, isMappingOpen, setSaveDisabled]);

    useEffect(() => {
        if (!isMappingOpen) return;

        const hasNothingToMap = !!conversionsError || !conversions?.length;

        setSaveDisabled(
            !!conversionsError ||
                (!hasNothingToMap &&
                    !areOpenAiMappingsValid(mappings, conversions)),
        );
    }, [
        isMappingOpen,
        conversionsError,
        conversions,
        mappings,
        setSaveDisabled,
    ]);

    const loadConversions = async (createdId) => {
        const response = await getOpenAiAdsConversions(createdId);

        if (!response.status) {
            setConversionsError(
                response.message ??
                    "OpenAI Ads conversions could not be loaded.",
            );
            setConversions([]);
            setEvents([]);
            setMappings([]);
            return;
        }

        const { all_conversions, all_events, auto_matched_events } =
            response.data?.conversions ?? {};

        setConversionsError(null);
        setConversions(all_conversions || []);
        setEvents(all_events || []);
        setMappings(normalizeAutoMatchedEvents(auto_matched_events));
    };

    const retryConversions = async () => {
        if (!destinationId) return;
        setModalLoading(true);
        await loadConversions(destinationId);
        setModalLoading(false);
    };

    const create = async () => {
        const pixelId = String(credentials.pixel_id ?? "").trim();
        const conversionsApiKey = String(
            credentials.conversions_api_key ?? "",
        ).trim();
        const advertiserApiKey = String(
            credentials.advertiser_api_key ?? "",
        ).trim();
        const blankMessage = cannotBeBlank(language);

        if (!pixelId || !conversionsApiKey || !advertiserApiKey) {
            form.setPixelIdError(pixelId ? "" : blankMessage);
            form.setConversionsKeyError(conversionsApiKey ? "" : blankMessage);
            form.setAdvertiserKeyError(advertiserApiKey ? "" : blankMessage);
            return;
        }

        setModalLoading(true);
        try {
            const response = await createDestination({
                create_pixel: form.createPixelStatus,
                pixel_id: pixelId,
                conversions_api_key: conversionsApiKey,
                advertiser_api_key: advertiserApiKey,
                destination_type: Destination.oaicapi,
            });

            const { conversionId, pixelId: createdPixelDestinationId } =
                getCreatedOpenAiDestinationIds(response);

            if (!response.status || !conversionId) {
                SwalAlertComp({
                    title: failedTitle,
                    message: response.message && response.message,
                    icon: "error",
                });
                return;
            }

            setDestinationId(conversionId);
            setPixelDestinationId(createdPixelDestinationId);
            await loadConversions(conversionId);
            form.resetCredentials();
            setIsMappingOpen(true);
        } finally {
            setModalLoading(false);
        }
    };

    const saveMappings = async () => {
        const mappingsToSave = conversions?.length ? mappings : [];

        if (
            conversions?.length &&
            !areOpenAiMappingsValid(mappingsToSave, conversions)
        ) {
            return;
        }

        setModalLoading(true);
        try {
            if (!destinationId) return;

            const response = await postOpenAiAdsMappings(
                destinationId,
                buildOpenAiMappingsPayload(mappingsToSave, conversions),
            );

            if (!response.status) {
                SwalAlertComp({
                    title: failedTitle,
                    message: response.message && response.message,
                    icon: "error",
                });
                return;
            }

            setIsMappingCompleted(true);
            await completeCreation(getCreatedIds());
            setIsMappingOpen(false);
        } finally {
            setModalLoading(false);
        }
    };

    const closeMapping = () => {
        const hasNothingToMap = !conversionsError && !conversions?.length;

        setIsMappingOpen(false);

        if (!isMappingCompleted && hasNothingToMap) {
            completeCreation(getCreatedIds());
            return;
        }

        hostActions.refresh();
        hostActions.close();
    };

    const isActive = destinationCode === Destination.oaicapi;
    const canCreate = !!(
        credentials?.pixel_id &&
        credentials?.conversions_api_key &&
        credentials?.advertiser_api_key
    );
    const hasNothingToMap = !!conversionsError || !conversions?.length;
    const canSaveMappings =
        !conversionsError &&
        (hasNothingToMap || areOpenAiMappingsValid(mappings, conversions));
    const createLabel =
        language?.buttons?.create_destination ?? "Create destination";
    const saveLabel = language?.buttons?.save ?? "Save";

    useHostFooterAction(
        isActive ? registerFooterAction : undefined,
        `${Destination.oaicapi}:form`,
        {
            label: createLabel,
            onSubmit: create,
            isDisabled: !canCreate,
            hidden: isMappingOpen,
        },
    );
    useHostFooterAction(
        isActive ? registerFooterAction : undefined,
        `${Destination.oaicapi}:mapping`,
        {
            label: saveLabel,
            onSubmit: saveMappings,
            isDisabled: !canSaveMappings,
            hidden: !isMappingOpen,
        },
    );

    return {
        conversions,
        events,
        mappings,
        setMappings,
        conversionsError,
        isMappingOpen,
        retryConversions,
        create,
        saveMappings,
        closeMapping,
        resetOnTypeChange,
    };
};

export default useOpenAiDestination;
